import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { Separator } from '../../components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Users, ShoppingCart, DollarSign, TrendingUp, Loader2, ArrowRight, Crown, Zap, Shield, Package, FileText, BarChart3 } from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const PLAN_COLORS = { free: '#94A3B8', pro: '#FF9A00', business: '#195259' };
const PLAN_LABELS = { free: 'Free', pro: 'Pro', business: 'Business' };

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadStats = useCallback(async () => {
    try {
      const res = await api.get('/admin/stats');
      setStats(res.data);
    } catch (err) {
      console.error('Failed to load admin stats');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  if (loading) return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {[1,2,3,4].map(i => <div key={i} className="h-28 rounded-xl bg-slate-100 animate-pulse" />)}
      </div>
    </div>
  );

  if (!stats) return <div className="text-center py-20 text-slate-500">Failed to load dashboard</div>;

  const planPieData = [
    { name: 'Free', value: stats.plan_distribution.free, color: PLAN_COLORS.free },
    { name: 'Pro', value: stats.plan_distribution.pro, color: PLAN_COLORS.pro },
    { name: 'Business', value: stats.plan_distribution.business, color: PLAN_COLORS.business },
  ].filter(d => d.value > 0);

  return (
    <div className="space-y-8 max-w-[1200px]" data-testid="admin-dashboard">
      {/* ==================== PLATFORM REVENUE (PRIMARY) ==================== */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Platform Revenue</h1>
        <p className="text-sm text-slate-500 mt-1">Your subscription-based revenue overview</p>
      </div>

      {/* Subscription KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Card className="border-slate-200 bg-gradient-to-br from-[#195259] to-[#1D6B75] text-white">
          <CardContent className="pt-5 pb-4">
            <p className="text-xs text-slate-300 font-medium uppercase tracking-wide">Monthly Revenue (MRR)</p>
            <p className="text-3xl font-bold mt-2">RM {stats.mrr.toLocaleString()}</p>
            <p className="text-xs text-slate-400 mt-1">RM {stats.arr.toLocaleString()} ARR</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Paid Subscribers</p>
                <p className="text-3xl font-bold text-slate-900 mt-2">{stats.total_subscribers}</p>
                <p className="text-xs text-slate-400 mt-1">{stats.paid_conversion_rate}% conversion</p>
              </div>
              <div className="h-10 w-10 rounded-lg flex items-center justify-center bg-amber-50 text-amber-600">
                <Crown className="h-5 w-5" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Total Creators</p>
                <p className="text-3xl font-bold text-slate-900 mt-2">{stats.total_creators}</p>
                <p className="text-xs text-slate-400 mt-1">+{stats.new_creators_30d} this month</p>
              </div>
              <div className="h-10 w-10 rounded-lg flex items-center justify-center bg-blue-50 text-blue-600">
                <Users className="h-5 w-5" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Active Creators</p>
                <p className="text-3xl font-bold text-slate-900 mt-2">{stats.active_creators}</p>
                <p className="text-xs text-slate-400 mt-1">{stats.total_creators > 0 ? Math.round(stats.active_creators / stats.total_creators * 100) : 0}% of total</p>
              </div>
              <div className="h-10 w-10 rounded-lg flex items-center justify-center bg-emerald-50 text-emerald-600">
                <TrendingUp className="h-5 w-5" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Plan Distribution + Signups Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Plan Distribution */}
        <Card className="lg:col-span-4 border-slate-200">
          <CardHeader>
            <CardTitle className="text-base text-slate-900">Plan Distribution</CardTitle>
            <CardDescription>Breakdown of creator subscriptions</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex justify-center mb-4">
              {planPieData.length > 0 ? (
                <ResponsiveContainer width={180} height={180}>
                  <PieChart>
                    <Pie data={planPieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value">
                      {planPieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <Tooltip formatter={(v, name) => [v, name]} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[180px] flex items-center justify-center text-slate-400 text-sm">No data</div>
              )}
            </div>
            <div className="space-y-3">
              {Object.entries(stats.plan_distribution).map(([plan, count]) => (
                <div key={plan} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="h-3 w-3 rounded-full" style={{ backgroundColor: PLAN_COLORS[plan] }} />
                    <span className="text-sm font-medium">{PLAN_LABELS[plan]}</span>
                    {plan !== 'free' && <span className="text-xs text-slate-400">RM {plan === 'pro' ? 49 : 149}/mo</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold">{count}</span>
                    <span className="text-xs text-slate-400">{stats.total_creators > 0 ? Math.round(count / stats.total_creators * 100) : 0}%</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* New Signups Trend */}
        <Card className="lg:col-span-8 border-slate-200">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-base text-slate-900">Creator Growth</CardTitle>
              <CardDescription>New registrations over the last 30 days</CardDescription>
            </div>
            <Badge variant="secondary">Last 30 days</Badge>
          </CardHeader>
          <CardContent>
            {stats.registration_trend.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={stats.registration_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={v => v.slice(5)} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" name="Signups" fill="#195259" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[260px] flex items-center justify-center text-slate-400 text-sm">No signups yet</div>
            )}
          </CardContent>
        </Card>
      </div>

      <Separator />

      {/* ==================== CREATOR ACTIVITY (SECONDARY) ==================== */}
      <div>
        <h2 className="text-lg font-semibold tracking-tight text-slate-700">Creator Activity</h2>
        <p className="text-sm text-slate-400 mt-0.5">Marketplace performance across all creators</p>
      </div>

      {/* Creator Activity KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Creator GMV', value: `RM ${stats.total_gmv.toLocaleString('en-MY', { minimumFractionDigits: 2 })}`, icon: DollarSign, color: 'text-emerald-600 bg-emerald-50' },
          { label: 'Total Orders', value: stats.total_orders, icon: ShoppingCart, color: 'text-blue-600 bg-blue-50' },
          { label: 'Products', value: stats.total_products, icon: Package, color: 'text-amber-600 bg-amber-50' },
          { label: 'Landing Pages', value: stats.total_landing_pages, icon: FileText, color: 'text-teal-600 bg-teal-50' },
        ].map(kpi => {
          const Icon = kpi.icon;
          return (
            <Card key={kpi.label} className="border-slate-200">
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-2">
                  <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${kpi.color}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-lg font-bold text-slate-900">{kpi.value}</p>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wide">{kpi.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* GMV Trend + Top Creators */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <Card className="lg:col-span-7 border-slate-200">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-semibold text-slate-700">GMV Trend (Creator Sales)</CardTitle>
            <Badge variant="secondary" className="text-[10px]">Last 30 days</Badge>
          </CardHeader>
          <CardContent>
            {stats.gmv_trend.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={stats.gmv_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={v => v.slice(5)} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v) => [`RM ${Number(v).toFixed(2)}`, 'GMV']} />
                  <Area type="monotone" dataKey="gmv" stroke="#64748B" fill="rgba(100,116,139,0.08)" strokeWidth={1.5} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-slate-400 text-sm">No sales data</div>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-5 border-slate-200">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-semibold text-slate-700">Top Creators</CardTitle>
            <Button variant="ghost" size="sm" className="text-xs" onClick={() => navigate('/admin/creators')}>
              View all <ArrowRight className="h-3 w-3 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {stats.top_creators.length > 0 ? (
              <div className="space-y-3">
                {stats.top_creators.map((c, i) => (
                  <div key={c.id} className="flex items-center gap-3 cursor-pointer hover:bg-slate-50 p-1.5 -mx-1.5 rounded-lg" onClick={() => navigate(`/admin/creators/${c.id}`)}>
                    <span className="text-xs font-mono text-slate-400 w-4">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{c.name}</p>
                      <p className="text-[10px] text-slate-400">@{c.handle} &middot; {c.orders} orders</p>
                    </div>
                    <Badge variant="secondary" className="text-[10px] capitalize" style={{ backgroundColor: `${PLAN_COLORS[c.plan || 'free']}20`, color: PLAN_COLORS[c.plan || 'free'] }}>
                      {PLAN_LABELS[c.plan || 'free']}
                    </Badge>
                    <span className="text-sm font-semibold text-slate-700">RM {c.revenue.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400 text-center py-8">No creator sales yet</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
